import {
  supportedExtraWallets,
  usePool,
  usePriceConverters,
  useUserExternalWallets,
} from '@trustpad/launchpad';
import { isNFTSale } from '@trustpad/launchpad/dist/pool/utils';

import React from 'react';
import { Button } from 'react-bootstrap';
import { FaCheck, FaPen } from 'react-icons/fa';
import { useSaleContributionData } from '~common/hooks/useSaleContributionData';

import { SecondaryCard } from '~common/UI';
import { tiers, oldLevels } from '~config/tiers';
import { Tier } from '~features/tiers';
import { addressShort, fNum } from '~utils';

import { TierWithMultiplier } from '../../../tiers/components/TierName';

const Row = ({ label, children }) => (
  <div className="flex justify-between items-center">
    <div>{label}</div>
    <div>{children}</div>
  </div>
);

// TODO: make a helper which would return the level based on the amount, use amount from contribution
export function UserFinishedSaleStatus({
  stakingTier,
  whitelisted,
}: {
  stakingTier: Tier;
  whitelisted: boolean;
}) {
  const {
    pool: { id, sale, token, fundToken, distribution },
  } = usePool();
  const symbol = token.symbol;
  const currencySymbol = fundToken.symbol;
  const { currencyToTokens } = usePriceConverters();
  const isNft = isNFTSale(sale);
  const allocationSymbol = isNft ? currencySymbol : symbol;

  const { contribution } = useSaleContributionData(id);
  if (!contribution) {
    return null;
  }
  const registeredTier: Tier = tiers[contribution.levelId] || oldLevels[1][contribution.levelId];

  const weControlClaimer = [
    'claim_us',
    'claim_linear',
    'claim_server',
    'airdrop_us',
  ].includes(
    (Array.isArray(distribution) ? distribution[0] : distribution).type,
  );
  const canLoseTokens =
    registeredTier.minAmount > stakingTier.minAmount && weControlClaimer;

  return (
    <SecondaryCard>
      <Row label="Current level">
        <TierWithMultiplier tier={stakingTier} />
      </Row>

      {contribution.isWhitelisted && <Row label="Whitelist">Whitelisted</Row>}

      {contribution.contributedTotal > 0 && (
        <Row label="Participated as">
          {contribution.levelId !== 'none' ? (
            <div>
              <div>
                <TierWithMultiplier tier={registeredTier} />
              </div>
              {id === 'bloktopia' && (
                <div className="text-right">
                  ({fNum(oldLevels[1][registeredTier.id].minAmount || 0)} TPAD)
                </div>
              )}
            </div>
          ) : (
            <>FCFS</>
          )}
        </Row>
      )}

      {registeredTier.random && (
        <Row label="Lottery">
          {contribution.isLotteryLevelWinner ? (
            <>You won! 🎉</>
          ) : (
            <>You lost 😞</>
          )}
        </Row>
      )}

      {false && !sale.refunded && weControlClaimer && (
        <Row label="Diamond Hands">
          {!canLoseTokens ? (
            <>
              You keep the tokens&nbsp;
              <FaCheck className="inline text-green-600 ml-1" />
            </>
          ) : (
            <>You'll lose the tokens</>
          )}
        </Row>
      )}

      {supportedExtraWallets.includes(token.chain) && (
        <ExtraWalletRow network={token.chain} />
      )}
    </SecondaryCard>
  );
}

function ExtraWalletRow({ network }: { network: string }) {
  const { wallets, toggleChanging } = useUserExternalWallets();

  return (
    <Row label={`${network} wallet`}>
      {wallets[network] && addressShort(wallets[network])}{' '}
      <Button
        size="sm"
        variant="outline-primary"
        className="opacity-50"
        onClick={() => toggleChanging()}>
        <FaPen />
      </Button>
    </Row>
  );
}
